import { type ChatboxAssistant } from '@soomo/lib/components/pageElements/AiChatbox/AIChatboxServiceContext';
import { type AiModel } from '@editor2/types/aiModel';

/** Catalog flags belong to Core, not to the authored assistant configuration. */
export const modelToAssistant = (model: AiModel): ChatboxAssistant => ({
	id: model.id,
	provider: model.provider,
	providerDisplayName: model.providerDisplayName,
	displayName: model.displayName
});

export const defaultAssistants = (models: AiModel[]): ChatboxAssistant[] =>
	models.filter((model) => model.default).map(modelToAssistant);

export const newChatboxConfig = (models?: AiModel[]): string => {
	const assistants = defaultAssistants(models ?? []);
	if (!assistants.length) {
		throw new Error('AI models must be loaded and have defaults before adding a chatbox.');
	}
	return JSON.stringify({ assistants }, null, 2);
};

export interface ModelOption {
	value: string;
	label: string;
	isDeprecated: boolean;
}

export const toModelOption = (assistant: ChatboxAssistant, models: AiModel[]): ModelOption => ({
	value: assistant.id,
	label: assistant.displayName || assistant.id,
	isDeprecated: models.some((model) => model.id === assistant.id && model.deprecated)
});

/** Config is free-form JSON; only assistant edits should ever rewrite it. */
const parseConfig = (config: string): { [key: string]: unknown } => {
	try {
		const parsed = JSON.parse(config || '{}');
		return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
	} catch {
		return {};
	}
};

const authoredAssistants = (config: string): ChatboxAssistant[] => {
	const { assistants } = parseConfig(config);
	return Array.isArray(assistants)
		? assistants.filter((assistant) => assistant && typeof assistant.id === 'string')
		: [];
};

export const configToSelectedModels = (config: string, models: AiModel[]): ModelOption[] => {
	const assistants = authoredAssistants(config);
	return (assistants.length ? assistants : defaultAssistants(models)).map((assistant) =>
		toModelOption(assistant, models)
	);
};

export const selectedModelsToConfig = (
	options: ModelOption[],
	config: string,
	models: AiModel[]
): string => {
	if (!options.length) throw new Error('Select at least one model.');
	const authored = authoredAssistants(config);
	const assistants = options.map(({ value }) => {
		// Keep authored labels, provider information and extra metadata, including unknown models.
		const existing = authored.find((assistant) => assistant.id === value);
		if (existing) return existing;
		const model = models.find((model) => model.id === value);
		if (!model) throw new Error(`AI model ${value} is not in the catalog.`);
		return modelToAssistant(model);
	});
	return JSON.stringify({ ...parseConfig(config), assistants }, null, 2);
};
